// Read/edit drawer for a single inventory item.
//
// Editors can tweak key fields (price, mileage, availability, etc.)
// and save; salespeople see the same fields in read-only mode. Raw
// source data is only shown to platform_admin — that's where it lives
// from CSV imports, and we don't want dealer users spelunking it.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { inventoryApi } from '../../api';
import {
  FRESHNESS_COLORS, AVAILABILITY_COLORS, CONFIDENCE_COLORS,
  AGENT_USABLE_COLORS,
} from './constants';

const EDITABLE_FIELDS = [
  'stock_number', 'vin', 'year', 'make', 'model', 'trim',
  'category', 'condition', 'price', 'mileage_km',
  'exterior_color', 'interior_color', 'url', 'availability_status',
];

export default function InventoryDetailDialog({
  item, open, onOpenChange, canEdit, isPlatformAdmin, onChanged,
}) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && item) {
      const seed = {};
      EDITABLE_FIELDS.forEach((f) => {
        seed[f] = item[f] ?? '';
      });
      setForm(seed);
    }
  }, [open, item]);

  if (!item) return null;

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const save = async () => {
    const patch = {};
    EDITABLE_FIELDS.forEach((f) => {
      const v = form[f];
      if (v === '' || v === null || v === undefined) return;
      if (f === 'price' || f === 'mileage_km' || f === 'year') {
        const num = Number(v);
        if (!Number.isNaN(num)) patch[f] = num;
      } else {
        patch[f] = v;
      }
    });
    if (!Object.keys(patch).length) {
      toast.info('No changes to save.');
      return;
    }
    setSaving(true);
    try {
      await inventoryApi.update(item.id, patch);
      toast.success('Saved');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {[item.year, item.make, item.model].filter(Boolean).join(' ') ||
              item.stock_number ||
              item.vin ||
              'Inventory item'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 mb-2">
          <span
            className={`px-2 py-0.5 rounded text-xs ${
              AVAILABILITY_COLORS[item.availability_status] || ''
            }`}
          >
            {item.availability_status}
          </span>
          <span
            className={`px-2 py-0.5 rounded text-xs ${
              FRESHNESS_COLORS[item.freshness_status] || ''
            }`}
          >
            {item.freshness_status}
          </span>
          {item.confidence_level && (
            <span
              className={`px-2 py-0.5 rounded text-xs capitalize ${
                CONFIDENCE_COLORS[item.confidence_level] || ''
              }`}
              title="Confidence — how reliable this row is for voice-agent use."
            >
              Confidence: {item.confidence_level}
            </span>
          )}
          {item.agent_usable !== null && item.agent_usable !== undefined && (
            <span
              className={`px-2 py-0.5 rounded text-xs ${
                AGENT_USABLE_COLORS[String(item.agent_usable)] || ''
              }`}
              title="Whether a voice agent may safely surface this row to callers."
            >
              Agent: {item.agent_usable ? 'usable' : 'not usable'}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {EDITABLE_FIELDS.map((f) => (
            <div key={f}>
              <Label className="capitalize">{f.replace(/_/g, ' ')}</Label>
              <Input
                value={form[f] ?? ''}
                onChange={setField(f)}
                disabled={!canEdit}
              />
            </div>
          ))}
        </div>

        {isPlatformAdmin && item.raw_source_data && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer">raw_source_data (admin)</summary>
            <pre className="bg-gray-900 text-gray-100 p-2 rounded overflow-auto max-h-48">
              {JSON.stringify(item.raw_source_data, null, 2)}
            </pre>
          </details>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {canEdit && (
            <Button disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
